import { useCallback, useEffect, useMemo, useState } from "react";
import { useAcknowledgePostProcedureOutcome } from "@src/packages/cycles/hooks/useCycles";
import type { PostProcedureOutcomeRoute } from "@src/packages/cycles/presenters/postProcedureOutcome";
import {
  resolveAcknowledgedStageForRoute,
  type PostProcedureOutcomeAcknowledgement,
  type PostProcedureOutcomeStage,
} from "@src/packages/cycles/presenters/postProcedureOutcomeState";

declare const __DEV__: boolean | undefined;

// Re-exported for existing importers. The definition lives with the rest of the
// ending vocabulary in the presenter; this file used to declare an identical
// copy, so the two could drift apart silently.
export type { PostProcedureOutcomeStage };

function readDevStage(): PostProcedureOutcomeStage | null {
  if (!__DEV__ || typeof window === "undefined") return null;

  const value = new URLSearchParams(window.location.search).get(
    "dev_outcome_stage",
  );
  return value === "recorded" || value === "pause" || value === "setup"
    ? value
    : null;
}

function isDevOutcomeFixture(): boolean {
  if (!__DEV__ || typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return [
    "dev_outcome_stage",
    "dev_outcome_result",
    "dev_outcome_source",
    "dev_outcome_verification",
  ].some((key) => params.has(key));
}

/**
 * The acknowledged surface this route may reach, or `null` when it has none.
 * See `resolveAcknowledgedStageForRoute` for why an unresolved or adverse route
 * must not be allowed to fall through to the negative-result pause.
 */
function nextStageForRoute(
  route: PostProcedureOutcomeRoute,
): Exclude<PostProcedureOutcomeStage, "recorded"> | null {
  return resolveAcknowledgedStageForRoute(route);
}

/**
 * Persists the patient's acknowledgement of a verified result through the
 * cycle API. Clinical acknowledgement must survive device changes and must
 * not be inferred from a local browser/device flag.
 */
export function usePostProcedureOutcomeStage({
  route,
  cycleId,
  outcomeId,
  acknowledgement,
}: {
  route: PostProcedureOutcomeRoute;
  cycleId?: string | null;
  outcomeId?: string | null;
  /** Server acknowledgement returned with the addressed outcome. */
  acknowledgement?: PostProcedureOutcomeAcknowledgement | null;
}) {
  const acknowledgeOutcome = useAcknowledgePostProcedureOutcome();
  const devStage = useMemo(() => readDevStage(), []);
  const canPersistRemotely = Boolean(
    cycleId && outcomeId && !isDevOutcomeFixture(),
  );
  const remoteStage =
    acknowledgement && acknowledgement.outcomeId === (outcomeId ?? null)
      ? nextStageForRoute(route)
      : null;
  const [stage, setStage] = useState<PostProcedureOutcomeStage>(
    devStage ?? remoteStage ?? "recorded",
  );
  const [isLoaded, setIsLoaded] = useState(false);
  /** True once a save was refused, until the next attempt. */
  const [didFail, setDidFail] = useState(false);

  useEffect(() => {
    if (devStage) {
      setStage(devStage);
      setIsLoaded(true);
      return;
    }

    if (remoteStage) {
      setStage(remoteStage);
      setIsLoaded(true);
      return;
    }

    setStage("recorded");
    setIsLoaded(true);
  }, [devStage, remoteStage]);

  const acknowledge = useCallback(() => {
    if (acknowledgeOutcome.isPending) return;
    setDidFail(false);

    // A route with no acknowledged surface must not be moved off `recorded`:
    // there is nowhere for it to go, and the old fallback sent it to the
    // negative-result pause. Refusing here is what lets callers drop the
    // defensive pin that kept these routes from acknowledging at all.
    const acknowledgedStage = nextStageForRoute(route);
    if (!acknowledgedStage) return;

    // Dev URL fixtures are intentionally local and never represent production
    // clinical state. Every real acknowledgement must be accepted by the
    // server before Home moves to its next surface.
    if (!canPersistRemotely) {
      if (devStage) setStage(acknowledgedStage);
      return;
    }

    void acknowledgeOutcome
      .mutateAsync({ cycleId: cycleId!, outcomeId: outcomeId! })
      .then(() => setStage(acknowledgedStage))
      .catch(() => {
        setStage("recorded");
        // Reverting in silence is what made this look like a button that does
        // nothing. The caller decides how to say so; it can only do that if it
        // is told.
        setDidFail(true);
      });
  }, [
    acknowledgeOutcome,
    canPersistRemotely,
    cycleId,
    devStage,
    outcomeId,
    route,
  ]);

  return {
    stage,
    isLoaded,
    isAcknowledging: acknowledgeOutcome.isPending,
    /**
     * Whether acknowledging can actually move Home.
     *
     * Two ways it cannot, and both used to be silent: this route has no
     * acknowledged surface to reach, or the acknowledgement cannot be persisted
     * — no `outcomeId`, or a dev fixture, which is deliberately local and may
     * never stand in for a clinical record. A caller must not offer a button
     * that would do nothing; it can only avoid that if it can ask.
     */
    canAcknowledge:
      Boolean(nextStageForRoute(route)) &&
      (canPersistRemotely || Boolean(devStage)),
    /** A save was refused. Cleared on the next attempt. */
    didFail,
    acknowledge,
    reset: () => setStage("recorded"),
  };
}
